// Parent Dashboard — a deliberately ADULT register: a clean, minimal report with
// no game chrome, a fixed light palette and Inter (not the game's font/theme), so
// it reads as a separate tool for grown-ups. Stats in plain English, a one-sentence
// weekly summary, a five-framework lesson tracker, and conversation starters.

import { useRef, useState } from 'react';
import { useGameState } from '@biz-kid/store';
import { useLife, useLifeComputed } from '../../life/store.js';
import { DECK } from '../builder/weeklyDecisionDeck.js';
import { CurriculumScreen } from '../../dev/CurriculumScreen.js';
import { APP_VERSION } from '../../dev/mechanicsCatalog.js';
import { KNOWLEDGE_CATALOG } from '../../life/KnowledgeCheck.js';
import {
  QUADRANT_INFO,
  MINDSET_LABEL,
  FRAMEWORK_ORDER,
  FRAMEWORK_INFO,
  weekCashflow,
  lessonsThisWeek,
} from './parentContent.js';

// A calm, professional palette — intentionally independent of the game theme.
const C = {
  bg: '#eef0f3',
  surface: '#ffffff',
  border: '#e1e5ea',
  rule: '#eceef1',
  ink: '#1b2430',
  ink2: '#5b6675',
  ink3: '#9aa4b1',
  accent: '#2c6e6a',
  accentSoft: '#e7f1f0',
  off: '#cfd5dd',
};
const FONT = "'Inter', system-ui, -apple-system, Segoe UI, sans-serif";

const MINDSET_QUESTION: Record<string, string> = {
  desire: 'Ask what they really want to achieve one day — and why it matters to them.',
  faith: 'Ask about something difficult they genuinely believe they could do.',
  persistence: 'Ask about a time they wanted to give up on something but kept going.',
  focus: 'Ask what helps them concentrate when something really matters.',
  gratitude: 'Ask what they felt thankful for this week.',
};

const money = (n: number) => `${n < 0 ? '−' : ''}B$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;

export function ParentDashboard({ onClose, onChangePin }: { onClose: () => void; onChangePin: () => void }) {
  // Hidden developer entry: long-press (≈700ms) the version number to open the
  // Curriculum Alignment export. Invisible to children; out of the way for parents.
  const [showCurriculum, setShowCurriculum] = useState(false);
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPress = () => {
    pressTimer.current = setTimeout(() => setShowCurriculum(true), 700);
  };
  const cancelPress = () => {
    if (pressTimer.current) clearTimeout(pressTimer.current);
    pressTimer.current = null;
  };

  const s = useLife();
  const c = useLifeComputed();
  const week = useGameState((g) => g.clock.week);
  const seed = useGameState((g) => g.rngSeed);
  const ledger = useGameState((g) => g.ledger);
  const name = useGameState((g) => g.profile.displayName) || 'Your child';
  const reputation = useGameState((g) => g.profile.reputation);
  const hasLoans = useGameState((g) => g.loans.length > 0);
  const hasInvestors = useGameState((g) => g.investors.length > 0);
  const businessCount = useGameState((g) => g.businesses.length);
  const inSlump = useGameState((g) => g.inSlump);
  // Select the stable arrays; map in render (mapping inside the selector returns a
  // fresh array each call and trips Zustand's snapshot-loop guard).
  const pending = useGameState((g) => g.pendingConsequences);
  const landed = useGameState((g) => g.landedConsequences);
  const pendFw = pending.map((p) => p.framework);
  const landFw = landed.map((l) => l.framework);

  const lastSettled = week > 1 ? week - 1 : null;
  const lastCf = lastSettled != null ? Math.round(weekCashflow(ledger, lastSettled)) : null;
  const card = DECK[(week + seed) % DECK.length]!;

  // top mindset sub-stat
  const mindEntries = Object.entries(s.mindset) as [keyof typeof s.mindset, number][];
  const topMind = mindEntries.reduce((a, b) => (b[1] > a[1] ? b : a));

  const lessons = lessonsThisWeek({
    thisWeekCardFramework: card.framework,
    recentConsequenceFrameworks: [...pendFw, ...landFw],
    ranBusinessThisWeek: businessCount > 0,
    ownsIncomeAsset: s.assets.some((a) => a.monthlyIncome > 0),
    hasLeverage: hasLoans || hasInvestors,
    hadSetback: inSlump || (lastCf != null && lastCf < 0),
    reputationMoved: reputation !== 50,
  });

  const did =
    lastCf == null
      ? 'began setting up their first business'
      : lastCf >= 0
        ? `ran their business and finished the week ${money(lastCf)} ahead`
        : `ran their business through a tough ${money(lastCf)} week`;
  const learned = c.freedomMode
    ? 'reached financial freedom in the game'
    : `moved to ${c.freedomPct}% of the way toward financial freedom`;
  const summary = `This week, ${name} ${did}, weighed up a ${card.framework.toLowerCase()} decision, and ${learned}.`;

  const starters = [
    FRAMEWORK_INFO[card.framework].question,
    lastCf == null
      ? 'Ask what kind of business they’d most love to run one day, and why.'
      : lastCf >= 0
        ? 'Their business did well this week. Ask what they think worked — and what they’d do with the profit.'
        : 'Their business had a hard week. Ask what they think went wrong, and what they’d try differently.',
    MINDSET_QUESTION[topMind[0]] ?? 'Ask them to tell you about something they’re proud of this week.',
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 90, overflowY: 'auto', background: C.bg, fontFamily: FONT, color: C.ink, WebkitFontSmoothing: 'antialiased' }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: 'clamp(20px, 4vw, 44px) clamp(16px, 4vw, 32px) 64px' }}>
        {/* masthead */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: 18, borderBottom: `1px solid ${C.border}` }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.ink3, fontWeight: 600 }}>Parent Dashboard</div>
            <h1 style={{ margin: '6px 0 0', fontSize: 24, fontWeight: 700, letterSpacing: '-0.01em' }}>{name}’s week</h1>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn onClick={onChangePin} subtle>Change PIN</Btn>
            <Btn onClick={onClose}>Close</Btn>
          </div>
        </div>

        {/* at a glance */}
        <Section title="At a glance">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 1, background: C.rule, border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
            <Stat label="Age" value={`${s.age}`} note="years old" />
            <Stat label="Net worth" value={money(c.netWorth)} note="assets minus what they owe" />
            <Stat label="Freedom Meter" value={`${c.freedomPct}%`} note="passive income vs their costs" />
            <Stat label="Quadrant" value={`${s.quadrant} · ${QUADRANT_INFO[s.quadrant].name}`} note={QUADRANT_INFO[s.quadrant].plain} wide />
            <Stat label="Strongest mindset" value={`${MINDSET_LABEL[topMind[0]] ?? topMind[0]}`} note={`${topMind[1]} / 100`} />
          </div>
        </Section>

        {/* weekly summary */}
        <Section title="This week, in a sentence">
          <p style={{ margin: 0, fontSize: 17, lineHeight: 1.6, color: C.ink, fontWeight: 400 }}>{summary}</p>
        </Section>

        {/* lesson tracker */}
        <Section title="Lessons that came up this week">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {FRAMEWORK_ORDER.map((f) => {
              const lit = lessons[f];
              return (
                <div key={f} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderRadius: 999, border: `1px solid ${lit ? C.accent : C.border}`, background: lit ? C.accentSoft : C.surface, color: lit ? C.accent : C.ink3 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: lit ? C.accent : C.off }} />
                  <span style={{ fontSize: 13.5, fontWeight: lit ? 600 : 500 }}>{f}</span>
                </div>
              );
            })}
          </div>
          <p style={{ margin: '12px 0 0', fontSize: 13, color: C.ink2, lineHeight: 1.55 }}>
            Lit badges are the money principles your child actually met in play this week. This week’s decision touched{' '}
            <b style={{ color: C.ink }}>{card.framework}</b> — {FRAMEWORK_INFO[card.framework].blurb}.
          </p>
        </Section>

        {/* Learning Progress — outcomes from the Knowledge Check questions
            asked by mentors after key learning moments. */}
        <Section title="Learning progress">
          <div style={{ display: 'grid', gap: 8 }}>
            {KNOWLEDGE_CATALOG.map((item) => {
              const lp = s.learningProgress ?? { triggered: {}, answers: [] };
              const triggered = !!lp.triggered[item.id];
              const ans = lp.answers.find((a) => a.id === item.id);
              const status = ans ? (ans.correct ? 'correct' : 'wrong') : triggered ? 'pending' : 'not-yet';
              const statusStyle = {
                correct: { fg: '#0f6f4a', bg: '#dbefe5', label: '✓ Answered correctly' },
                wrong:   { fg: '#a13a30', bg: '#f4dedb', label: '✗ Answered — explained' },
                pending: { fg: '#8b6d22', bg: '#f4ead0', label: '⌛ Question pending (between weeks)' },
                'not-yet': { fg: C.ink3,  bg: C.surface, label: 'Not yet — will fire after the moment' },
              }[status];
              return (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>{item.label}</div>
                    <div style={{ fontSize: 12, color: C.ink3, marginTop: 2 }}>
                      {item.framework} framework · reward: +5 {item.stat}
                      {ans ? ` · asked week ${ans.askedAtWeek}` : ''}
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 11.5, fontWeight: 700, padding: '4px 10px', borderRadius: 999,
                      background: statusStyle.bg, color: statusStyle.fg, whiteSpace: 'nowrap',
                    }}
                  >
                    {statusStyle.label}
                  </span>
                </div>
              );
            })}
          </div>
          <p style={{ margin: '12px 0 0', fontSize: 12.5, color: C.ink2, lineHeight: 1.55 }}>
            One question fires per save, automatically, after each learning moment is lived. Wrong answers are explained, never penalised — the goal is the conversation, not the score.
          </p>
        </Section>

        {/* conversation starters */}
        <Section title="Talk about it — three questions to ask">
          <ol style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 10 }}>
            {starters.map((q, i) => (
              <li key={i} style={{ display: 'flex', gap: 12, padding: '14px 16px', background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: C.accent, fontWeight: 700, fontSize: 15 }}>{i + 1}</span>
                <span style={{ fontSize: 14.5, lineHeight: 1.55, color: C.ink }}>{q}</span>
              </li>
            ))}
          </ol>
        </Section>

        <p style={{ marginTop: 28, paddingTop: 16, borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.ink3, lineHeight: 1.6 }}>
          Everything in Biz Kid is play money. There are no real purchases, no advertising to your child, and no chat with strangers. Nothing leaves this device.
        </p>

        {/* Version line — long-press reveals the developer Curriculum Alignment export. */}
        <div style={{ marginTop: 16, fontSize: 11.5, color: C.ink3 }}>
          Biz Kid{' '}
          <span
            onPointerDown={startPress}
            onPointerUp={cancelPress}
            onPointerLeave={cancelPress}
            onContextMenu={(e) => e.preventDefault()}
            style={{ userSelect: 'none', cursor: 'default', touchAction: 'none' }}
            title="Biz Kid"
          >
            v{APP_VERSION}
          </span>
        </div>
      </div>

      {showCurriculum && <CurriculumScreen onClose={() => setShowCurriculum(false)} />}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginTop: 28 }}>
      <h2 style={{ margin: '0 0 12px', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.ink3, fontWeight: 600 }}>{title}</h2>
      {children}
    </section>
  );
}

function Stat({ label, value, note, wide }: { label: string; value: string; note: string; wide?: boolean }) {
  return (
    <div style={{ background: C.surface, padding: '14px 16px', gridColumn: wide ? '1 / -1' : undefined }}>
      <div style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: C.ink3, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, marginTop: 4, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>{value}</div>
      <div style={{ fontSize: 12.5, color: C.ink2, marginTop: 2, lineHeight: 1.4 }}>{note}</div>
    </div>
  );
}

function Btn({ children, onClick, subtle }: { children: React.ReactNode; onClick: () => void; subtle?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '8px 14px',
        borderRadius: 7,
        border: `1px solid ${subtle ? C.border : C.ink}`,
        background: subtle ? C.surface : C.ink,
        color: subtle ? C.ink2 : '#fff',
        fontFamily: FONT,
        fontWeight: 600,
        fontSize: 13,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
